import { useAppNavigate } from "../../../../../hooks/useAppNavigate";
import { Grid, Typography, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useOnboarding } from "../../contexts/onboarding.context";
import { WizardHeading } from "../../../../../core/components/WizardHeading";
import { PrimaryButton } from "../../../../layout/Button/Button";
import { FormActions } from "../../../../../core/components/FormActions";
import { createSubscription } from "../../../../../client/api/public/stripe";
import { List, ListItem, ListItemIcon, ListItemText, RadioGroup, Radio, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import Alert from "@mui/material/Alert";
import { CreditCard, Receipt } from "@mui/icons-material";
import { verbalTheme } from "../../../../layout/themes/verbal.theme";
import { Product } from "../../entities/Products";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";

const getStripePromise = (): Promise<Stripe | null> | null => {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    if (!publishableKey) {
        return null;
    }

    return loadStripe(publishableKey);
};
const PaymentMethod: React.FC = () => {
    const { details, plan } = useOnboarding();
    const [method, SetMethod] = useState<string | undefined>();
    const [loading, setLoading] = useState<boolean>(false);
    const { push } = useAppNavigate();
    const stripe = useStripe();
    const [error, setError] = useState<string>();
    const handlePaymentMethodSelection = async () => {
        if (loading) return;
        try {
            setLoading(true);
            const res = await createSubscription({
                product: plan as Product,
                details: details,
                paymentMethod: method as string,
            });
            const body = await res.json();
            switch (method) {
                case "Invoice":
                    push(`/subscription/success?subscription_id=${body?.subscriptionId}`);
                    break;
                case "Card":
                    stripe?.redirectToCheckout({
                        sessionId: body?.subscriptionId,
                    });
            }
        } catch (error) {
            setError("Unable to complete setup at this time. Please try again later.");
            setLoading(false);
        }
    };
    const useStyles = makeStyles(
        createStyles({
            card: {
                border: "2px solid",
                borderColor: "#32383e44",
                boxShadow: "#000000 0px 0px 0px 0px, #00000099 0px 1px 2px 0px,",
                borderRadius: "8px",
                marginTop: "1em",
                inset: -1,
                cursor: "pointer",
            },
            selectedcard: {
                borderColor: verbalTheme.palette.primary.main,
            },
        }),
    );
    const classes = useStyles();
    return (
        <>
            <form>
                <WizardHeading heading={"How would you prefer to pay?"} />
                <Grid container alignItems="flex-start" spacing={2}>
                    <Grid item xs={12}>
                        <RadioGroup
                            aria-label="Select a payment method"
                            name="paymentmethod"
                            defaultValue=""
                            onChange={(event: React.ChangeEvent, value: string) => SetMethod(value)}
                        >
                            <List>
                                <ListItem
                                    className={`${classes.card}${
                                        method === "Invoice" ? ` ${classes.selectedcard}` : ""
                                    }`}
                                    alignItems="flex-start"
                                    onClick={() => SetMethod("Invoice")}
                                >
                                    <ListItemIcon>
                                        <Receipt />
                                    </ListItemIcon>
                                    <Grid container alignItems="flex-start" spacing={2}>
                                        <Grid item xs={12}>
                                            <ListItemText primary="Invoice" />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography>
                                                We will send you an invoice at the end of your trial period to be paid
                                                by bank transfer
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Radio value="Invoice" checked={method === "Invoice"} />
                                </ListItem>
                                <ListItem
                                    className={`${classes.card}${method === "Card" ? ` ${classes.selectedcard}` : ""}`}
                                    alignItems="flex-start"
                                    onClick={() => SetMethod("Card")}
                                >
                                    <ListItemIcon>
                                        <CreditCard />
                                    </ListItemIcon>
                                    <Grid container alignItems="flex-start" spacing={2}>
                                        <Grid item xs={12}>
                                            <ListItemText primary="Credit / Debit Card" />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography>
                                                On the next screen you will be asked to enter your card details, once
                                                your free trial has ended you card will be automatically charged
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Radio value="Card" checked={method === "Card"} />
                                </ListItem>
                            </List>
                        </RadioGroup>
                    </Grid>
                    <Grid item xs={12} style={{ color: "rgba(0, 0, 0, 0.54)" }}>
                        Input fields marked with an asterisk (*) are required
                    </Grid>
                    <Grid item xs={12}>
                        <FormActions
                            right={
                                <>
                                    <PrimaryButton
                                        size="large"
                                        disabled={!method || loading}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            handlePaymentMethodSelection();
                                        }}
                                    >
                                        {loading ? (
                                            <>
                                                <CircularProgress size={20} color="inherit" />
                                                <span style={{ marginLeft: "1em" }}>Loading...</span>
                                            </>
                                        ) : (
                                            "Continue"
                                        )}
                                    </PrimaryButton>
                                </>
                            }
                        />
                    </Grid>
                </Grid>
            </form>
            <Box mt={4}>{error && <Alert severity="error">{error}</Alert>}</Box>
        </>
    );
};
const WrappedPaymentMethod: React.FC = () => {
    const stripePromise = React.useMemo(() => getStripePromise(), []);

    if (!stripePromise) {
        return <Alert severity="error">Stripe is not configured. Please contact support.</Alert>;
    }

    return (
        <Elements stripe={stripePromise}>
            <PaymentMethod />
        </Elements>
    );
};
export default WrappedPaymentMethod;
